"use client";

import {
  detectListStructureInText,
  plainTextToList,
} from "@/service/text/list";
import { trans } from "@/service/i18n/i18n";
import { RestrictedIcd10Resource } from "@/components/icd10/restricted-icd10-resource";
import { Paywall } from "@/components/common/paywall";
import { AccessInfo } from "@/components/common/access-info";

import { Icd10VideoCard } from "@/components/icd10/icd10-resource/cards/icd10-video-card";
import { Icd10PublicationCard } from "@/components/icd10/icd10-resource/cards/icd10-publication-card";
import { Icd10ClinicalCaseCard } from "@/components/icd10/icd10-resource/cards/icd10-clinical-case-card";
import { Icd10TutorialCard } from "@/components/icd10/icd10-resource/cards/icd10-tutorial-card";
import { Icd10CalculatorCard } from "@/components/icd10/icd10-resource/cards/icd10-calculator-card";
import { Icd10TableOfContents } from "@/components/icd10/icd10-table-of-contents";
import { Icd10EbookCard } from "@/components/icd10/icd10-resource/cards/icd10-ebook-card";
import { Icd10CodeExtended } from "@/components/icd10/icd10-code-extended";
import { Button } from "@/components/ui/button";
import { SuperLink as Link } from "@/components/common/super-link";
import { CardSlider } from "@/components/common/card-slider";
import { PageSection } from "@/components/common/page-section/page-section";
import { Icd10Inn } from "@/components/icd10/icd10-resource/cards/icd10-inn";
import { Icd10Med } from "@/components/icd10/icd10-resource/cards/icd10-med";
import { Icd10CourseCard } from "@/components/icd10/icd10-resource/cards/icd10-course-card";
import { createPath } from "@/service/routing/routing";

export const Icd10Resource = ({
  code: {
    id,
    name,
    slug,
    description,
    description2,
    code,
    parent,
    diseaseKind,
    articles,
    children,
    tutorials,
    calculators,
    clinicalCases,
    videos,
    inns,
    groups,
    ebooks,
    courses,
    hasMoreMedGroups,
  },
}: {
  code: ICD10VM;
}) => {
  const groupsNotEmpty = groups.length > 0;
  const innsNotEmpty = inns.length;

  const Anchor = ({ id }: { id: string }) => (
    <div className={"relative -top-12"} id={id}></div>
  );

  return (
    <div style={{ "--content-width": "var(--content-width-small)" }}>
      <div
        className={"@container mx-auto max-w-[var(--content-width)]"}
        data-slug={slug}
      >
        <header className={"mb-4"}>
          {diseaseKind && (
            <p className={`text-remedium-blue mb-1 text-sm font-semibold`}>
              {diseaseKind}
            </p>
          )}
          <h1 className={"flex flex-col gap-2"}>
            <span className={"text-foreground text-2xl font-semibold"}>
              {code}
            </span>
            <span className={`text-sidebar-foreground text-base font-normal`}>
              {" "}
              {name}
            </span>
          </h1>
          {description && (
            <div
              className={`text-sidebar-foreground my-4 text-base font-normal`}
            >
              {description}
            </div>
          )}
          {description2 && (
            <div className={"my-4"}>
              {detectListStructureInText(description2) ? (
                <ol className={"my-4 list-decimal pl-5 md:pl-8"}>
                  {plainTextToList(description2).map((item) => (
                    <li
                      className={"mb-4 text-sm text-zinc-900 md:text-base"}
                      key={item}
                    >
                      {item}
                    </li>
                  ))}
                </ol>
              ) : (
                description2
              )}
            </div>
          )}
        </header>

        <Icd10TableOfContents
          articles={articles}
          calculators={calculators}
          clinicalCases={clinicalCases}
          groups={groups}
          inns={inns}
          tutorials={tutorials}
          videos={videos}
          ebooks={ebooks}
          code={code}
          courses={courses}
        />

        {children.length > 0 && (
          <div className={"my-12"}>
            {children.map((code) => (
              <Icd10CodeExtended key={code.id} code={code} />
            ))}
          </div>
        )}

        {videos.length > 0 && (
          <>
            <Anchor id={"anchor-for-videos"} />
            <PageSection heading={trans("icd10.video")}>
              <Paywall>
                <CardSlider
                  items={videos.map((data: VideoPreviewType) => (
                    <Icd10VideoCard data={data} code={code} key={code} />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {courses?.length > 0 && (
          <>
            <Anchor id={"anchor-for-courses"} />
            <PageSection heading={trans("icd10.courses")}>
              <Paywall>
                <CardSlider
                  items={courses.map((data: CoursePreviewVM) => (
                    <Icd10CourseCard data={data} code={code} key={code} />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {articles.length > 0 && (
          <>
            <Anchor id={"anchor-for-articles"} />
            <PageSection heading={trans("icd10.publications")}>
              <Paywall>
                <CardSlider
                  items={articles.map((article: ArticlePreviewVM) => (
                    <Icd10PublicationCard
                      key={article.title}
                      code={code}
                      article={article}
                    />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {clinicalCases.length > 0 && (
          <>
            <Anchor id={"anchor-for-clinicalCases"} />
            <PageSection heading={trans("icd10.clinical_cases")}>
              <Paywall>
                <CardSlider
                  items={clinicalCases.map((clinicalCase) => (
                    <Icd10ClinicalCaseCard
                      code={code}
                      clinicalCase={clinicalCase}
                      key={clinicalCase.name}
                    />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {ebooks.length > 0 && (
          <>
            <Anchor id={"anchor-for-ebooks"} />
            <PageSection heading={trans("icd10.ebooks")}>
              <Paywall>
                <CardSlider
                  items={ebooks.map((ebook: EbookPreviewVM) => (
                    <Icd10EbookCard
                      code={code}
                      ebook={ebook}
                      key={ebook.name}
                    />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {tutorials.length > 0 && (
          <>
            <Anchor id={"anchor-for-tutorials"} />
            <PageSection heading={trans("icd10.tutorials")}>
              <Paywall>
                <CardSlider
                  items={tutorials.map((tutorial: TutorialPreviewVM) => (
                    <Icd10TutorialCard
                      code={code}
                      tutorial={tutorial}
                      key={tutorial.title}
                    />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {calculators.length > 0 && (
          <>
            <Anchor id={"anchor-for-calculators"} />
            <PageSection heading={trans("icd10.calculators")}>
              <Paywall>
                <CardSlider
                  items={calculators.map((calculator: CalculatorPreviewVM) => (
                    <Icd10CalculatorCard
                      code={code}
                      calculator={calculator}
                      key={calculator.title}
                    />
                  ))}
                />
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {innsNotEmpty > 0 && (
          <>
            <Anchor id={"anchor-for-inns"} />
            <PageSection heading={trans("icd10.ingredient")}>
              <p className={`text-colors-zinc-600 text-sm font-normal`}>
                {trans("icd10.ingredients_for_code")} <strong>{code}</strong>
              </p>
              <Paywall>
                <div className={"my-4 flex flex-wrap gap-2"}>
                  {inns.map((inn) => (
                    <Icd10Inn code={code} inn={inn} key={inn.name} />
                  ))}
                </div>
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource type={"meds"} />
              </AccessInfo>
            </PageSection>
          </>
        )}

        {groupsNotEmpty && (
          <>
            <Anchor id={"anchor-for-groups"} />
            <PageSection heading={trans("icd10.med")}>
              <p className={`text-colors-zinc-600 text-sm font-normal`}>
                {trans("icd10.meds_for_code")} <strong>{code}</strong>
              </p>
              <Paywall>
                <div className={"my-4 flex flex-col flex-wrap gap-2"}>
                  {groups.map((med) => (
                    <Icd10Med med={med} code={code} key={med.name} />
                  ))}
                </div>
                {hasMoreMedGroups && (
                  <Button size={"sm"} asChild={true}>
                    <Link href={createPath("meds.icd10", { id })}>
                      Zobacz pełną listę
                    </Link>
                  </Button>
                )}
              </Paywall>
              <AccessInfo>
                <RestrictedIcd10Resource type={"meds"} />
              </AccessInfo>
            </PageSection>
          </>
        )}
      </div>
    </div>
  );
};
